import React, { useCallback, useEffect, useState } from 'react';
import { SearchSuffix, Tip } from '@components';
import { COLORS } from '@constants';
import { CAMPAIGN_TYPE_ID } from '@enums';
import { useStore } from '@hooks';
import { Icon, Input, Typography } from '@ui';
import { formatString } from '@utils';
import { Flex } from 'antd';
import _ from 'lodash';
import { reaction, toJS } from 'mobx';
import { observer } from 'mobx-react';
import {
    ScrollContainer,
    StyledListItem,
    StyledTag
} from '../../../../pages/Step1/components/CardExpandedContent/styled';
import { StyledWrapper } from './styled';

const SpecificTypesSelection = observer(({ recalculatePriority }) => {
    const store = useStore();
    const [selectedListItems, setSelectedListItems] = useState([]);
    const [listItemsToShow, setListItemsToShow] = useState([]);
    const [searchValue, setSearchValue] = useState('');

    const {
        campaign,
        getPreviewProducts,
        setSelectedCollectionIds,
        setSelectedProductTypeIds,
        getAccountProducts,
        resetSearch
    } = store.campaignWizardStore;
    const { getAccountProductTypes, getAccountCollections, currentAccount } = store.accountsStore;

    const isSpecificCollection = campaign.catalogType === CAMPAIGN_TYPE_ID.SPECIFIC_COLLECTIONS;
    const isSpecificProductType = campaign.catalogType === CAMPAIGN_TYPE_ID.SPECIFIC_PRODUCT_TYPES;

    const { productTypes, productCollections } = currentAccount;
    const { productTypes: campaignProductTypes, collections: campaignCollections } = campaign;
    const tipText = isSpecificCollection
        ? 'Product collections are being updated on daily basis'
        : 'Product types are being updated on hourly basis';
    const placeholder = isSpecificProductType ? 'Search product types' : 'Search collections';
    const prefix = isSpecificProductType ? 'product-type' : 'collection';

    const defaultSelectedProductTypesIds = campaignProductTypes.map(({ typeId }) => typeId);
    const defaultSelectedCollectionsIds = campaignCollections.map(({ collectionId }) => collectionId);

    useEffect(() => {
        const disposer = reaction(
            () => {
                if (isSpecificProductType) {
                    return toJS(store.accountsStore.currentAccount.productTypes);
                }

                if (isSpecificCollection) {
                    return toJS(store.accountsStore.currentAccount.productCollections);
                }

                return [];
            },
            (data) => {
                setListItemsToShow(data);

                if (isSpecificProductType) {
                    setSelectedListItems(toJS(data.filter((type) => defaultSelectedProductTypesIds.includes(type.id))));
                    return;
                }

                if (isSpecificCollection) {
                    setSelectedListItems(
                        toJS(data.filter((collection) => defaultSelectedCollectionsIds.includes(collection.id)))
                    );
                }
            }
        );
        return () => disposer();
    }, [store]);

    const debouncedOnSearchChange = useCallback(
        _.debounce((string) => {
            if (isSpecificProductType) {
                return setListItemsToShow(
                    toJS(productTypes.filter(({ title }) => title.toLowerCase().includes(string.toLowerCase())))
                );
            }

            if (isSpecificCollection) {
                return setListItemsToShow(
                    toJS(productCollections.filter(({ title }) => title.toLowerCase().includes(string.toLowerCase())))
                );
            }
        }, 500),
        []
    );

    const debouncedRecalculatePriority = useCallback(
        _.debounce(() => {
            recalculatePriority();
        }, 500),
        []
    );

    const onSearchChange = (e) => {
        setSearchValue(e.target.value);
        debouncedOnSearchChange(e.target.value);
    };

    const onTagClose = (item) => {
        const newSelectedItems = selectedListItems.filter((i) => i.id !== item.id);

        // At least 1 type or collection should be selected
        if (!newSelectedItems?.length) {
            return;
        }

        setSelectedListItems(newSelectedItems);
        resetSearch();
        refreshProductsList(newSelectedItems);
        debouncedRecalculatePriority();
    };

    const onListItemClick = (item) => {
        let newSelectedItems;
        if (!selectedListItems.find((i) => i.id === item.id)) {
            newSelectedItems = [...selectedListItems, item];
            setSelectedListItems(newSelectedItems);
        } else {
            newSelectedItems = selectedListItems.filter((i) => i.id !== item.id);

            if (!newSelectedItems?.length) {
                return;
            }

            setSelectedListItems(newSelectedItems);
        }

        resetSearch();
        refreshProductsList(newSelectedItems);
        debouncedRecalculatePriority();
    };

    // Function to refresh products list with temporary selections
    const refreshProductsList = (selectedItems) => {
        // Only refresh if we actually have selected items
        if (!selectedItems || !selectedItems.length) return;

        if (isSpecificProductType) {
            const ids = selectedItems.map((item) => item.id);
            setSelectedProductTypeIds(ids);
        } else if (isSpecificCollection) {
            const ids = selectedItems.map((item) => item.id);
            setSelectedCollectionIds(ids);
        }

        // Call store method to fetch products with temporary selections
        getPreviewProducts();
        getAccountProducts({ withCampaignProductsMeta: true });
    };

    useEffect(() => {
        (async () => {
            const promises = [
                isSpecificProductType ? getAccountProductTypes() : null,
                isSpecificCollection ? getAccountCollections() : null
            ].filter(Boolean);

            await Promise.all(promises);
        })();
    }, []);

    return (
        <StyledWrapper>
            <Tip text={tipText} />
            <Flex vertical gap={16}>
                <Input
                    label={placeholder}
                    data-e2e={`edit-camp-page_${prefix}_input`}
                    value={searchValue}
                    onChange={onSearchChange}
                    onPressEnter={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                    }}
                    allowClear={{
                        clearIcon: (
                            <Icon
                                data-e2e={`edit-camp-page_clear-${prefix}-title_x-btn`}
                                style={{ marginTop: 3 }}
                                size='24px'
                                component={() => <Icon.CloseIcon width={24} height={24} />}
                            />
                        )
                    }}
                    suffix={!searchValue && <SearchSuffix onSetSearchValue={setSearchValue} />}
                />
                <Flex gap={16} wrap='wrap'>
                    {selectedListItems.map((item) => (
                        <StyledTag
                            onClose={(e) => {
                                e.preventDefault();
                                onTagClose(item);
                            }}
                            closeIcon={
                                <Icon
                                    data-e2e={`edit-camp-page_selected-${prefix}-${formatString(item.title)}_x-btn`}
                                    component={() => <Icon.CloseIcon />}
                                    color={COLORS.$white}
                                />
                            }
                            key={item.id}
                            data-e2e={`edit-camp-page_selected-${prefix}-${formatString(item.title)}_tag`}
                        >
                            {item.title}
                        </StyledTag>
                    ))}
                </Flex>
                <div style={{ maxHeight: 250, overflow: 'hidden' }}>
                    <ScrollContainer>
                        <Flex vertical style={{ paddingRight: 10 }}>
                            {listItemsToShow.map((item) => (
                                <StyledListItem
                                    onClick={(e) => {
                                        e.preventDefault();
                                        onListItemClick(item);
                                    }}
                                    key={item.id}
                                    data-e2e={`edit-camp-page_listed-${prefix}-${formatString(item.title)}_tag`}
                                >
                                    <Flex align='center'>
                                        <Typography.Text type='descriptor1' style={{ marginRight: 'auto' }}>
                                            {item.title}
                                        </Typography.Text>
                                        {selectedListItems.find((i) => i.id === item.id) && (
                                            <Icon.CheckIcon width='24px' height='24px' color={COLORS.$pink} />
                                        )}
                                    </Flex>
                                </StyledListItem>
                            ))}
                        </Flex>
                    </ScrollContainer>
                </div>
            </Flex>
        </StyledWrapper>
    );
});

export default SpecificTypesSelection;
